<template>
  <div class="container">
    <div class="api-sdk-section">
      <div class="container">
        <div class="api-sdk-content">
          <div class="section-header">
            <div class="section-text">
              <h2>Integrate Spatial AI into your workflows</h2>
            </div>
          </div>

          <div class="integration-grid">
            <div class="integration-card" v-for="item in section">
              <div class="integration-icon" :key="item.title">
                <component :is="item.icons" class="integration-icon" />
              </div>
              <h3>{{ item.title }}</h3>
              <ul>
                <li v-for="point in item.points" :key="point">{{ point }}</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { RiSmartphoneLine, RiFlaskLine, RiCodeLine } from "@remixicon/vue";
import { onMounted } from "vue";
const section = [
  {
    icons: RiSmartphoneLine,
    title: "Mobile SDK",
    points: [
      "Seamless iOS & Android SDKs",
      "Easy setup with minimal code",
      "Secure data handling & encryption",
      "Optimized for performance & battery usage",
      "Regular updates & version compatibility",
    ],
  },
  {
    icons: RiCodeLine,
    title: "Embed iframe",
    points: [
      "Responsive iframe code",
      "Cross-platform support",
      " Optimized for speed & low bandwidth",
      "Copy-paste embed code",
      "Built-in analytics & engagement tracking",
    ],
  },
];
</script>
<style scoped lang="scss">
.container {
  font-family: "Book21", "Sharp Grotesk", sans-serif;
  /* fallback */
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem;
}

// API & SDK Section Styles
.api-sdk-section {
  padding: 8rem 0;
  background: linear-gradient(
    135deg,
    rgba(31, 29, 32, 0.1) 0%,
    rgba(0, 0, 0, 0.8) 100%
  );

  @include respond-below(sm) {
    padding: 4rem 0;
  }

  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 1rem;
  }

  .api-sdk-content {
    text-align: center;
  }

  .section-header {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 2rem;
    margin-bottom: 4rem;

    @include respond-below(sm) {
      flex-direction: column;
      gap: 1rem;
      margin-bottom: 3rem;
    }
    .section-text {
      display: flex;
      flex-direction: column;
      align-items: center;

      @include respond-below(sm) {
        text-align: center;
      }

      h2 {
        font-size: clamp(1.6rem, 5vw, 4rem);
        font-weight: $fw-bold;
        margin: 0 0 1rem 0;
        background-image: radial-gradient(
          47.08% 208.33% at 79.71% 128.33%,
          rgba(237, 125, 255, 0.8) 11.69%,
          rgba(108, 84, 255, 0.8) 35.44%,
          rgba(249, 245, 245, 0.8) 70.24%
        );
        background-clip: text;
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        color: transparent;

        // @include respond-below(sm) {
        //   font-size: clamp(2rem, 8vw, 2.5rem);
        // }
      }
    }
  }

  .integration-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));

    gap: 2rem;
    margin-bottom: 4rem;

    @include respond-below(sm) {
      gap: 1.5rem;
      margin-bottom: 3rem;
    }
  }

  .integration-card {
    background: rgba(5, 5, 5, 0.884);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 1rem;
    padding: 2rem;
    text-align: left;
    transition: all 0.3s ease;
    backdrop-filter: blur(10px);
    // @include respond-below(sm) {
    //   width: 280px;
    // }

    &:hover {
      transform: translateY(-8px);
      border: 1px solid rgba(138, 43, 226, 0.3);
      box-shadow: 0 15px 30px rgba(138, 43, 226, 0.4);
    }

    .integration-icon {
      width: 3.125rem;
      height: 3.125rem;
      border-radius: 0.75rem;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 1.5rem;
      @include respond-below(sm) {
        width: 2.2rem;
        height: 2.2rem;
      }
    }

    h3 {
      font-size: 1.5rem;
      font-weight: $fw-semibold;
      color: white;
      margin: 0 0 1rem 0;
      @include respond-below(sm) {
        font-size: 1.1rem;
      }
    }

    p {
      font-size: 1.2rem;
      color: rgba(255, 255, 255, 0.7);
      line-height: 1.6;
      margin: 0 0 1.5rem 0;
      @include respond-below(sm) {
        font-size: 0.9rem;
      }
    }

    ul {
      list-style: none;
      padding: 0;
      margin: 0;

      li {
        position: relative;
        padding-left: 1.5rem;
        margin-bottom: 0.5rem;
        color: rgba(255, 255, 255, 0.415);
        font-size: 0.9rem;

        &::before {
          content: "✓";
          position: absolute;
          left: 0;
          color: #ffffff;
          font-weight: bold;
        }

        &:last-child {
          margin-bottom: 0;
        }
      }
    }
  }

  .cta-section {
    text-align: center;
    padding: 3rem 2rem;
    background: rgba(5, 5, 5, 0.1);
    border-radius: 1rem;
    border: 1px solid rgba(255, 255, 255, 0.1);

    h3 {
      font-size: 2.5rem;
      font-weight: $fw-bold;
      color: white;
      margin: 0 0 1rem 0;

      @include respond-below(sm) {
        font-size: 1.2rem;
      }
    }

    p {
      font-size: 1.1rem;
      color: rgba(255, 255, 255, 0.7);
      margin: 0 0 2rem 0;
      max-width: 600px;
      margin-left: auto;
      margin-right: auto;
      margin-bottom: 2rem;

      @include respond-below(sm) {
        font-size: 0.9rem;
      }
    }

    .cta-button {
      background: inherit;
      color: white;
      border: 1px solid rgba(255, 255, 255, 0.1);
      padding: 1rem 2.5rem;
      font-size: 1.1rem;
      font-weight: $fw-semibold;
      border-radius: 0.5rem;
      cursor: pointer;
      transition: all 0.3s ease;

      &:hover {
        transform: translateY(-2px);
        border: 1px solid rgba(138, 43, 226, 0.3);
        box-shadow: 0 15px 30px rgba(138, 43, 226, 0.4);
      }
    }
  }
}
</style>
